import React from 'react';
import { formatPercent, formatScore } from './formatters';
import './RiskBreakdown.css';

/**
 * RiskBreakdown — renders the 3-tier risk profile section.
 *
 * Props:
 *   riskBreakdown {Object} — result.riskBreakdown from computeMVA output
 *   adjustmentOverrideUsed {boolean} — result.adjustmentOverrideUsed
 *
 * Semantics: scores 0..1, HIGHER = STRONGER business (premium).
 * adjustment = 0.65 − weighted; positive adjustment REDUCES multiples.
 */

/** Returns a clamped 0-100 integer, or null when score is non-finite. */
const meterValue = (score) =>
  Number.isFinite(score) ? Math.min(100, Math.max(0, Math.round(score * 100))) : null;

function RiskBreakdown({ riskBreakdown = {}, adjustmentOverrideUsed = false }) {
  const {
    industryRisk = {},
    sizeRisk = {},
    companyRisk = {},
    weighted,
    adjustment,
  } = riskBreakdown;

  const subcategories = companyRisk.subcategories || [];

  const hasValidWeighted = Number.isFinite(weighted);

  function adjustmentSentence() {
    if (!Number.isFinite(adjustment)) {
      return null;
    }
    if (adjustment > 0) {
      return `Your risk profile reduces the market multiples by ${formatPercent(adjustment)}.`;
    }
    if (adjustment < 0) {
      return `Your risk profile earns a premium of ${formatPercent(Math.abs(adjustment))} on the market multiples.`;
    }
    return 'Your risk profile applies no adjustment to the market multiples.';
  }

  function TierRow({ label, weight, score, ariaLabel }) {
    const mv = meterValue(score);
    return (
      <div className="risk-breakdown-tier">
        <span className="risk-breakdown-tier-label">{label}</span>
        <span className="risk-breakdown-tier-weight">{formatPercent(weight)}</span>
        {mv !== null ? (
          <div className="risk-breakdown-meter-track">
            <div
              role="progressbar"
              aria-label={ariaLabel}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={mv}
              className="risk-breakdown-meter-fill"
              style={{ width: `${mv}%` }}
            />
          </div>
        ) : null}
        <span className="risk-breakdown-score-text">{formatScore(score)}/100</span>
      </div>
    );
  }

  const sentence = adjustmentSentence();

  return (
    <section className="risk-breakdown-section">
      <h3 className="risk-breakdown-heading">Your risk profile</h3>

      <div className="risk-breakdown-tiers">
        <TierRow
          label="Industry"
          weight={industryRisk.weight}
          score={industryRisk.score}
          ariaLabel="Industry strength score"
        />
        <TierRow
          label="Size"
          weight={sizeRisk.weight}
          score={sizeRisk.score}
          ariaLabel="Size strength score"
        />
        <TierRow
          label="Company"
          weight={companyRisk.weight}
          score={companyRisk.score}
          ariaLabel="Company strength score"
        />
      </div>

      <div className="risk-breakdown-combined">
        {hasValidWeighted ? (
          <>
            <p className="risk-breakdown-weighted-line">
              Weighted strength score: {formatScore(weighted)}/100
            </p>
            {sentence && (
              <p className="risk-breakdown-adjustment-line">{sentence}</p>
            )}
          </>
        ) : (
          <p className="risk-breakdown-unavailable-note">
            Risk profile unavailable for this calculation.
          </p>
        )}

        <p className="risk-breakdown-footnote">
          Applies to the SDE- and EBITDA-multiple methods only.
        </p>

        {adjustmentOverrideUsed && (
          <p className="risk-breakdown-override-note">
            An appraiser override was applied to this calculation.
          </p>
        )}
      </div>

      {subcategories.length > 0 ? (
        <details className="risk-breakdown-subcategory-details">
          <summary className="risk-breakdown-subcategory-summary">
            Company score detail
          </summary>
          <ul className="risk-breakdown-subcategory-list">
            {subcategories.map((sub, idx) => {
              const smv = meterValue(sub.score);
              return (
                <li key={idx} className="risk-breakdown-tier">
                  <span className="risk-breakdown-tier-label">{sub.label}</span>
                  {smv !== null ? (
                    <div className="risk-breakdown-meter-track">
                      <div
                        role="progressbar"
                        aria-label={`${sub.label} strength score`}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={smv}
                        className="risk-breakdown-meter-fill"
                        style={{ width: `${smv}%` }}
                      />
                    </div>
                  ) : null}
                  <span className="risk-breakdown-score-text">
                    {formatScore(sub.score)}/100
                  </span>
                </li>
              );
            })}
          </ul>
        </details>
      ) : (
        <p className="risk-breakdown-placeholder-note">
          Company-level detail will appear here as we expand the Lite model.
        </p>
      )}
    </section>
  );
}

export default RiskBreakdown;
